import type { ReactNode } from 'react';
import { Box, Text, Timeline } from '@citibox-panel/ui';
import type { DeliveryDetail } from '@citibox-panel/api-client';
import { buildDeliveryTimeline } from '../lib/buildDeliveryTimeline';
import { formatDeliveryDateTime } from '../../../lib/formatDeliveryDateTime';

export interface DeliveryTimelineProps {
  detail: DeliveryDetail;
  // «Ampliar custodia», handed down already wired by the layout (which owns the flow) and hung off
  // the pending-expiry row. A ReactNode rather than the context + five handlers because this
  // component is the ANCHOR, not a participant: it decides WHERE the control goes and learns
  // nothing about what it does. Absent when the delivery is not extendable.
  extendCustodyControl?: ReactNode;
}

// The trazabilidad section. buildDeliveryTimeline produces the ordered rows with raw ISO
// instants (it stays timezone-agnostic); the view localizes each to the locker's zone
// (ADR 0011, read off `location.timezone` since CBX-10954) here — a dateless row formats to an
// em dash. No rows (a legacy parcel with
// no dates) → the empty-state copy instead of an empty list.
export function DeliveryTimeline({ detail, extendCustodyControl }: DeliveryTimelineProps) {
  const items = buildDeliveryTimeline(detail).map((item) => ({
    ...item,
    datetime: formatDeliveryDateTime(item.datetime ?? null, detail.location.timezone),
    // The control belongs to the row whose date it changes — the pending «Expira la recogida»
    // estimate, the only `estimated` row the builder ever emits. No such row (any status but
    // `stored`) and the control is simply dropped rather than landing on an unrelated milestone.
    ...(extendCustodyControl !== undefined && item.variant === 'estimated'
      ? { action: extendCustodyControl }
      : {}),
  }));

  return (
    <Box>
      {/* TODO: i18n — copy en ES hasta Fase 1. */}
      <Text variant="overline" color="secondary" bold>
        TRAZABILIDAD
      </Text>
      {items.length === 0 ? (
        <Text variant="body2" color="secondary">
          Sin trazabilidad disponible
        </Text>
      ) : (
        <Timeline items={items} />
      )}
    </Box>
  );
}
